/*
 * Plakkende koopbalk (sections/sticky-buy-bar.liquid), component C uit docs/page-spec.md.
 * Leest de opties uit de offer box van rm-product-main en verschijnt zodra de koopknop in de
 * koopkolom uit beeld is (IntersectionObserver). Een keuze in de balk zet dezelfde optie in de
 * offer box en andersom, via het event "rm:offer-change" op document.
 *
 * De knop legt NIETS in de winkelwagen (CLAUDE.md, regel 7): een tik scrolt naar het koopblok
 * en zet de focus op de gekozen optie, zodat de klant daar zelf bevestigt.
 * Scrollen gaat met scrollIntoView en scroll-margin-top: Horizon laat vanaf 990px .page-wrapper
 * scrollen, niet het venster.
 */
const OFFER_EVENT = 'rm:offer-change';

class RmStickyBar extends HTMLElement {
  connectedCallback() {
    this.main = document.querySelector('rm-product-main');
    this.mainButton = document.querySelector('[data-buy-button]');
    this.trigger = this.querySelector('.rm-sticky__selected');
    this.list = this.querySelector('.rm-sticky__options');
    this.labelEl = this.querySelector('[data-sticky-label]');
    this.metaEl = this.querySelector('[data-sticky-meta]');
    this.goButton = this.querySelector('[data-sticky-go]');
    this.goLabel = this.querySelector('[data-sticky-btn-label]');
    if (!this.main || !this.trigger || !this.list || !this.goButton) return;

    this.options = Array.from(this.main.querySelectorAll('[data-offer-option]')).map((el) => ({
      index: Number(el.dataset.index),
      label: el.dataset.label || '',
      button: el.dataset.button || '',
      badge: el.dataset.badge || '',
      price: el.dataset.price || '',
      perNight: el.dataset.perNight || '',
    }));
    if (this.options.length === 0) return;

    this.buildList();
    // Offer box: de gekozen radio. Koopblok met tegels: de standaardcombinatie.
    const checked = this.main.querySelector('.rm-offer__input:checked');
    const fallback = this.main.querySelector('[data-offer-option][data-default="true"]');
    this.apply(checked ? Number(checked.value) : fallback ? Number(fallback.dataset.index) : this.options[0].index);
    this.removeAttribute('hidden');

    this.trigger.addEventListener('click', () => this.toggleList());
    this.trigger.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
        event.preventDefault();
        this.openList();
      }
    });
    this.list.addEventListener('click', (event) => {
      const item = event.target.closest('[data-index]');
      if (item) this.choose(Number(item.dataset.index));
    });
    this.list.addEventListener('keydown', (event) => this.onListKey(event));
    this.goButton.addEventListener('click', (event) => this.goToBuyBox(event));

    this.onDocumentClick = (event) => {
      if (!this.contains(event.target)) this.closeList();
    };
    this.onDocumentKey = (event) => {
      if (event.key === 'Escape' && this.classList.contains('is-open')) {
        this.closeList();
        this.trigger.focus();
      }
    };
    this.onOffer = (event) => {
      if (event.detail && event.detail.source !== 'sticky-bar') this.apply(event.detail.index);
    };
    this.onResize = () => this.updateHeight();
    document.addEventListener('click', this.onDocumentClick);
    document.addEventListener('keydown', this.onDocumentKey);
    document.addEventListener(OFFER_EVENT, this.onOffer);
    window.addEventListener('resize', this.onResize);

    if (this.mainButton) {
      this.observer = new IntersectionObserver(([entry]) => this.setVisible(!entry.isIntersecting), { threshold: 0 });
      this.observer.observe(this.mainButton);
    } else {
      this.setVisible(true);
    }
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.onDocumentClick);
    document.removeEventListener('keydown', this.onDocumentKey);
    document.removeEventListener(OFFER_EVENT, this.onOffer);
    window.removeEventListener('resize', this.onResize);
    if (this.observer) this.observer.disconnect();
    this.setVisible(false);
  }

  buildList() {
    this.list.replaceChildren();
    this.items = this.options.map((option) => {
      const item = document.createElement('li');
      item.className = 'rm-sticky__option';
      item.setAttribute('role', 'option');
      item.setAttribute('tabindex', '-1');
      item.dataset.index = String(option.index);
      const label = document.createElement('span');
      label.textContent = option.badge ? `${option.label} (${option.badge})` : option.label;
      const price = document.createElement('span');
      price.className = 'rm-sticky__option-price';
      price.textContent = option.price;
      item.append(label, price);
      this.list.appendChild(item);
      return item;
    });
  }

  apply(index) {
    const option = this.options.find((item) => item.index === index) || this.options[0];
    this.index = option.index;
    this.labelEl.textContent = option.badge ? `${option.label} (${option.badge})` : option.label;
    const suffix = this.dataset.perNightSuffix || '';
    this.metaEl.textContent = option.perNight ? `${option.perNight} ${suffix}`.trim() : '';
    this.metaEl.hidden = !option.perNight;

    this.goLabel.replaceChildren();
    // Heeft de optie een eigen knoptekst (offer box), dan geldt die voor lang en kort.
    const long = document.createElement('span');
    long.className = 'rm-sticky__btn-long';
    long.textContent = (option.button || this.dataset.buttonLabel || '[price]').replace('[price]', option.price);
    const short = document.createElement('span');
    short.className = 'rm-sticky__btn-short';
    short.textContent = (option.button || this.dataset.buttonLabelShort || this.dataset.buttonLabel || '[price]').replace('[price]', option.price);
    this.goLabel.append(long, short);

    (this.items || []).forEach((item) => {
      item.setAttribute('aria-selected', Number(item.dataset.index) === option.index ? 'true' : 'false');
    });
  }

  choose(index) {
    this.apply(index);
    this.closeList();
    this.trigger.focus();
    document.dispatchEvent(new CustomEvent(OFFER_EVENT, { detail: { source: 'sticky-bar', index } }));
  }

  goToBuyBox(event) {
    const anchor = (this.dataset.target || 'buy-box').replace(/^#/, '');
    const target = document.getElementById(anchor);
    if (!target) return;
    event.preventDefault();
    this.closeList();
    const header = parseFloat(getComputedStyle(document.body).getPropertyValue('--header-height')) || 0;
    target.style.scrollMarginTop = `${header + 16}px`;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    target.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });
    const checked = target.querySelector('.rm-offer__input:checked');
    if (checked) checked.focus({ preventScroll: true });
  }

  toggleList() {
    if (this.classList.contains('is-open')) {
      this.closeList();
    } else {
      this.openList();
    }
  }

  openList() {
    this.classList.add('is-open');
    this.list.hidden = false;
    this.trigger.setAttribute('aria-expanded', 'true');
    const selected = this.items.find((item) => item.getAttribute('aria-selected') === 'true') || this.items[0];
    if (selected) selected.focus();
  }

  closeList() {
    this.classList.remove('is-open');
    this.list.hidden = true;
    this.trigger.setAttribute('aria-expanded', 'false');
  }

  onListKey(event) {
    const current = this.items.indexOf(document.activeElement);
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      (this.items[Math.min(this.items.length - 1, current + 1)] || this.items[0]).focus();
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      (this.items[Math.max(0, current - 1)] || this.items[0]).focus();
    } else if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      if (current > -1) this.choose(Number(this.items[current].dataset.index));
    } else if (event.key === 'Tab') {
      this.closeList();
    }
  }

  setVisible(visible) {
    this.classList.toggle('is-visible', visible);
    document.documentElement.classList.toggle('rm-sticky-visible', visible);
    if (visible) this.updateHeight();
    if (!visible) this.closeList();
  }

  updateHeight() {
    document.documentElement.style.setProperty('--rm-sticky-height', `${this.offsetHeight}px`);
  }
}

if (!customElements.get('rm-sticky-bar')) {
  customElements.define('rm-sticky-bar', RmStickyBar);
}
